<template>
  <div class="heatmap_area"
       v-if="infoData">
    <div class="inner">
      <template v-if="mode !== 'only'">
        <heatmap-control :mode="mode"
                         :aid="aid"
                         :infoData="infoData"
                         :referer="referer"
                         :device="device"
                         :eventType="eventType"
                         :eventSource="eventSource"
                         :applyData="applyData"
                         :metric="metric"
                         :isFrequency="isFrequency"
                         @onCoordsClick="coordsClick = $event"
                         @onRefererChange="onOptionChange('referer', $event)"
                         @onDeviceChange="onOptionChange('device', $event)"
                         @onEventTypeChange="onOptionChange('eventType', $event)"
                         @onEventSourceChange="onOptionChange('eventSource', $event)"
                         @onFrequency="onFrequency($event)"
                         @refererData="refererData = $event"/>

        <div v-if="showCtaTip"
             class="heatmap_tip cta_tip">
          <p>
            <i class="icon icon--tip"></i>
            <span class="tip" v-html="$t('cta.tip')"></span>
          </p>
          <button type="button" class="close" @click="showCtaTip = false">
            <i class="icon icon--close14"></i>
          </button>
        </div>
        <div v-if="scrollTip && eventType === 'scroll'"
             class="heatmap_tip scroll_tip">
          <p>
            <i class="icon icon--tip"></i>
            <span class="tip" v-html="$t('reporting.scroll.tip')"></span>
          </p>
          <button type="button" class="close" @click="scrollTip = false">
            <i class="icon icon--close14"></i>
          </button>
        </div>
      </template>
      <div class="view-area-wrap"
           :class="{ with_info_bar: hasInfoBar,
                     with_heatmap_downloading: isHeatmapDownloading === true,
                     with_mobile: ['tap', 'zoom', 'swipe'].includes(eventType),
                     only_mode: mode === 'only' }"
          @mouseover.capture="isHidePv = true"
          @mouseleave="isHidePv = false">
        <heatmap-type v-if="['tap', 'zoom', 'swipe'].includes(eventType)"
                      :eventType="eventType"
                      :heatmapType="realHeatmapType"
                      @setHeatmapType="setHeatmapType" />
        <div class="view_area"
             ref="container"
             :class="[`device-${device}`, { 'hidden-x': fitMode === 'width' || (fitMode !== 'width' && containerScale <= 1) }]"
             :style="{'margin-top': isHeatmapDownloading === true ? -heatmapInfoHeight + 'px' : 0,
                      'padding-top': mode !== 'only' && ['tap', 'zoom', 'swipe'].includes(eventType) ? 42 + 'px' : 0}">
          <div class="view_wrap"
               :style="{ width: containerWidth + 'px' }">
            <div :id="'heatmapArea' + _uid"
                 @mouseenter="isHeatmapOnHover = true"
                 @mouseleave="isHeatmapOnHover = false">
              <heatmap-info v-if="(mode === 'only' && (!ctaData || !ctaData.coords || ctaReportData)) || isHeatmapDownloading === true"
                            :infoData="infoData"
                            :referer="referer"
                            :device="device"
                            :eventType="eventType"
                            :eventSource="eventSource"
                            :heatmapType="realHeatmapType"
                            :applyData="applyData"
                            :ctaData="ctaData"
                            :ctaReportData="ctaReportData"
                            :metric="metric"
                            :abData="abData"
                            :freqTotalPv="totalPv"
                            :pathData="{ conversion: conversion, group: activityStatistics }"
                            @setHeatmapInfoHeight="setHeatmapInfoHeight" />
              <div class="view"
                   v-if="containerWidth > 0 && !isLoading"
                   :style="containerStyle">
                <div class="view-heatmap"
                     :style="{ transform: isHeatmapDownloading ? `scale(${heatmapDownloadScale})` : null,
                               width: isHeatmapDownloading ? `${1 / heatmapDownloadScale * 100}%` : null}">

                  <heatmap-svg
                    v-if="svgData && eventType !== 'scroll'"
                    :svgData="svgData"
                    :device="device"
                    :eventType="eventType"
                    :scale="scale"
                    :containerScale="containerScale"
                    :captureHeight="captureHeight"
                  />
                  <heatmap-grid
                    v-if="gridData && (eventType === 'tap' || eventType === 'zoom')"
                    :gridData="gridData"
                    :heatmapType="realHeatmapType"
                    :device="device"
                    :eventType="eventType"
                    :scale="scale"
                    :containerScale="containerScale"
                  />
                  <heatmap-swipe
                    v-if="swipeData && eventType === 'swipe'"
                    :swipeData="swipeData"
                    :heatmapType="realHeatmapType"
                    :device="device"
                    :eventType="eventType"
                    :scale="scale"
                    :containerWidth="containerWidth"
                    :captureHeight="captureHeight"
                    :captureWidth="captureWidth"
                    :containerScale="containerScale"
                  />
                  <heatmap-scroll
                    v-if="scrollData && eventType === 'scroll'"
                    :scrollData="scrollData"
                    :scale="scale"
                    :fitMode="fitMode"
                    :containerWidth="containerWidth"
                    :containerHeight="containerHeight"
                  />
                  <heatmap-stream
                    v-if="streamData && eventType === 'stream'"
                    :streamData="streamData"
                    :device="device"
                    :scale="scale"
                    :containerScale="containerScale"
                    :containerWidth="containerWidth"
                    :containerHeight="containerHeight"
                    :activeSelector="activeSelector"
                    :userActionData="userActionData"
                    :convertionType="conversion"
                    :scrollContainer="$refs.container"
                    :isHeatmapDownloading="isHeatmapDownloading"
                    @onUserActionDataChange="$emit('onUserActionDataChange', $event)"
                    @onHeatmapCenterChange="onHeatmapCenterChange"
                  />
                  <heatmap-coords
                      v-if="coordsDataText && isEditStatus"
                      :mode="mode"
                      :coordsData="coordsDataText"
                      :device="device"
                      :eventType="'edit'"
                      :scale="scale"
                      :containerWidth="containerWidth"
                      :containerHeight="containerHeight"
                      :activeSelector="activeSelector"
                      :ctaData="ctaData"
                      :isHeatmapDownloading="isHeatmapDownloading"
                      @onHeatmapCenterChange="onHeatmapCenterChange"
                      @onCtaDataChange="$emit('onCtaDataChange', $event)"
                  />
                  <heatmap-coords
                    v-if="coordsData && ['click', 'tap', 'move', 'zoom', 'swipe', 'scroll'].includes(eventType)"
                    :mode="mode"
                    :coordsData="coordsData"
                    :device="device"
                    :eventType="eventType"
                    :scale="scale"
                    :containerWidth="containerWidth"
                    :containerHeight="containerHeight"
                    :activeSelector="activeSelector"
                    :ctaData="ctaData"
                    :coordsClick="coordsClick"
                    @onCoordsClick="coordsClick = $event"
                    :isHeatmapDownloading="isHeatmapDownloading"
                    @onHeatmapCenterChange="onHeatmapCenterChange"
                    @onCtaDataChange="$emit('onCtaDataChange', $event)"
                    @onToolTipHover="toolTipHover = $event"
                  />
                  <heatmap-avg-fold
                    v-if="avgFoldData"
                    :avgFoldData="avgFoldData"
                    :scale="scale"
                    :fitMode="fitMode"
                  />
                </div>
              </div>
              <div class="heatmap-image--footer"
                   v-if="mode === 'only' || isHeatmapDownloading === true">
                <span>Dive into data and UX with</span>
                <i class="icon icon--logo bitmap"></i>
              </div>
            </div>
            <div v-if="isLoading"
                 class="loading_indicator">
              <div class="loading circle">
                <svg x="0px" y="0px" viewBox="0 0 150 150">
                  <circle class="loading-inner" cx="75" cy="75" r="60"/>
                </svg>
                <p class="loading_notice">{{ $t('common.loadingDot') }}</p>
              </div>
            </div>
          </div>
        </div>
        <template v-if="mode !== 'only'">
          <div class="floating-menu"
               :class="{mobile: ['tap', 'zoom', 'swipe'].includes(eventType)}">
            <div class="total-pv"
                 :class="{
                    hide: (metric.pv === null && !isTotalPv) ||
                          (activeSelector && !isTotalPv) ||
                          (coordsClick && !isTotalPv) ||
                          (userActionData && (userActionData.hoverIndex !== null || userActionData.activeIndex !== null) && !isTotalPv) ||
                          isHeatmapOnHover
                  }"
                 @mouseover.stop="isTotalPv = true" @mouseleave="isTotalPv = false">
              <span class="number" >
                {{
                  isHidePv || isTotalPv
                    ? isTotalPv
                      ? totalPv
                      : null
                    : totalPv
                }} <span>PV</span>
              </span>
            </div>
          <heatmap-floating-menu :fitMode="fitMode"
                                 :eventType="eventType"
                                 :isHeatmapOnHover="isHeatmapOnHover"
                                 :isLoading="isLoading"
                                 @onFitModeChange="onFitModeChange"
                                 @onHeatmapDownload="onHeatmapDownload" />
          </div>
          <div v-if="['aType', 'bType'].includes(mode)" style="position: relative;">
            <span class="abLabel" :class="{ bLabel: mode === 'bType' }">
              {{ mode === 'aType' ? 'A' : 'B' }}
            </span>
          </div>
          <heatmap-dialog v-if="dialogType"
                          :dialogType="dialogType"
                          @onCloseClick="dialogType = null"/>
          <heatmap-dialog v-if="isCaptureHeightExceed"
                          dialogType="captureHeightExceed"
                          @onCloseClick="isCaptureHeightExceed = false"/>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { EventBus } from '@/libs/event-bus'
import { mapMutations, mapState } from 'vuex'
import AnimateScroll from '@/libs/animate-scroll'
import { parse, startOfDay, endOfDay } from 'date-fns'
import {
  getBasicInfoData,
  getCaptureImageUrl,
  getAvgFoldData,
  getHeatmapData,
  getCoordsData,
  getSwipeData,
  getScrollData,
  getStreamPathData,
  getCtaData,
  getDailyDevice,
  getHeatmapImage,
  getHourBasicInfoData,
  getFreqInfoData
} from '@/api/report'
import {
  normalizeClickData,
  normalizeScrollData,
  normalizeSwipeData,
  normalizeStreamData,
  normalizeTextData, heatmapDataCheckByCapture
} from '@/libs/report-data-normalizer'
import HeatmapControl from './HeatmapControl'
import HeatmapInfo from './HeatmapInfo'
import HeatmapType from './HeatmapType'
import HeatmapSvg from './HeatmapSvg'
import HeatmapAvgFold from './HeatmapAvgFold'
import HeatmapCoords from './HeatmapCoords'
import HeatmapGrid from './HeatmapGrid'
import HeatmapSwipe from './HeatmapSwipe'
import HeatmapScroll from './HeatmapScroll'
import HeatmapStream from './HeatmapStream'
import HeatmapFloatingMenu from './HeatmapFloatingMenu'
import HeatmapDialog from './HeatmapDialog'
import { NEW_MOBILE_HEATMAP_STARTDATE, DEVICE_DESCKTOP, DEVICE_PHONE, DEVICE_TABLET, DESKTOP_SCROLL_HEIGHT, MOBILE_SCROLL_HEIGHT } from '@/libs/constants'
import { BEUSABLY_URL, thousand } from '@/libs/utils'

export default {
  name: 'Heatmap',
  components: {
    HeatmapControl,
    HeatmapType,
    HeatmapInfo,
    HeatmapSvg,
    HeatmapAvgFold,
    HeatmapCoords,
    HeatmapGrid,
    HeatmapSwipe,
    HeatmapScroll,
    HeatmapStream,
    HeatmapFloatingMenu,
    HeatmapDialog
  },
  props: {
    mode: {
      required: true,
      validator (value) {
        return ['default', 'compareA', 'compareB', 'cta', 'aType', 'bType', 'only'].includes(value)
      }
    },
    aid: {
      type: String,
      required: true
    },
    captureAid: {
      type: String,
      required: false
    },
    orgAid: {
      type: String,
      required: false
    },
    initialReferer: {
      type: String,
      default: 'all'
    },
    initialDevice: {
      type: String,
      required: false
    },
    initialEventType: {
      type: String,
      required: false
    },
    initialEventSource: {
      type: String,
      required: false
    },
    initialHeatmapType: {
      type: Number,
      required: false
    },
    initialCampaign: {
      required: false
    },
    initialUtm: {
      required: false
    },
    initialUtid: {
      required: false
    },
    abMode: {
      required: false
    },
    abTestType: {
      required: false,
      default: null
    },
    isEditStatus: {
      default: false,
      required: false
    },
    activeSelector: [String, null],
    userActionData: [Object, null],
    conversion: null,
    activityStatistics: {
      default: 'median'
    },
    abData: null,
    syncedOptions: {
      type: [Object, null],
      default: null
    },
    heatmapType: {
      default: 0
    },
    isDownloading: null,
    hourData: {
      type: String,
      default: null
    },
    freqPv: {
      type: Array,
      default: null
    },
    startIdx: {
      type: Number,
      default: null
    },
    endIdx: {
      type: Number,
      default: null
    },
    isReportClick: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      infoData: null,
      imageUrl: null,
      referer: this.initialReferer,
      device: this.initialDevice,
      eventType: this.initialEventType,
      campaign: null,
      utid: null,
      eventSource: this.initialEventSource || 'all',
      realHeatmapType: this.initialHeatmapType || 0,
      fitMode: 'width',
      svgData: null,
      gridData: null,
      swipeData: null,
      coordsData: null,
      scrollData: null,
      avgFoldData: null,
      streamData: null,
      captureWidth: 0,
      captureHeight: 0,
      containerWidth: 0,
      containerHeight: 0,
      containerScale: 1,
      isLoading: false,
      showCtaTip: this.mode === 'cta',
      scrollTip: null,
      dialogType: null,
      ctaPv: null,
      isCaptureHeightExceed: false,
      isHeatmapOnHover: false,
      heatmapInfoHeight: 308,
      ctaReportData: null,
      isHeatmapDownloading: false,
      maxHeatmapDownloadHeight: 32250,
      heatmapDownloadScale: 1,
      coordsDataText: null,
      toolTipHover: false,
      coordsClick: false,
      hourInfoDta: null,
      reportData: null,
      reportPeriodChange: false,
      isHidePv: false,
      isFrequency: false,
      freqData: null,
      freqMeanData: null,
      isTotalPv: false,
      freqTotalPv: null,
      refererData: null,
      init: true
    }
  },
  computed: {
    ...mapState([
      'userData',
      'reportPeriod',
      'applyData',
      'ctaData',
      'captureImageTimestamp',
      'timeseriesData',
      'freqRange'
    ]),
    scale () {
      if (this.captureWidth && this.containerWidth) {
        return this.containerWidth / this.captureWidth
      }
      return 0
    },
    ratio () {
      if (this.captureWidth && this.captureHeight) {
        return this.captureWidth / this.captureHeight
      }
      return 0
    },
    containerStyle () {
      return {
        width: (this.isHeatmapDownloading ? this.containerWidth * this.heatmapDownloadScale : this.containerWidth) + 'px',
        height: (this.isHeatmapDownloading ? this.containerHeight * this.heatmapDownloadScale : this.containerHeight) + 'px',
        backgroundImage: `url(${this.imageUrl})`,
        overflow: (((this.eventType === 'tap' && !this.isReportClick) || (this.eventType === 'zoom' && !this.isReportClick)) && this.toolTipHover === false) ? 'hidden' : null
      }
    },
    metric () {
      const data = {
        pv: 0,
        uv: 0
      }

      if (this.infoData) {
        let targetData = this.infoData.device[this.device]

        if (targetData) {
          if (this.referer !== 'all') {
            targetData = targetData.refer.find(item => item.refer === this.referer)

            if (!targetData) {
              return data
            }
          }

          let pvField = 'pv'
          let uvField = 'uv'

          switch (this.eventSource) {
            case 'new':
              pvField = 'newPv'
              uvField = 'newUv'
              break
            case 'return':
              pvField = 'returnPv'
              uvField = 'returnUv'
              break
          }

          data.pv = targetData[pvField] || 0
          data.uv = targetData[uvField] || 0
        }
      }
      this.$emit('onMetricChange', data)
      return data
    },
    bab () {
      if (!this.abMode) {
        switch (this.mode) {
          case 'aType':
            return false
          case 'bType':
            return true
          default:
            return null
        }
      } else {
        switch (this.abMode) {
          case 'aType':
            return false
          case 'bType':
            return true
          default:
            return null
        }
      }
    },
    defaultParams () {
      const params = {
        aid: this.aid,
        refer: this.referer,
        device: this.device,
        conversion: this.conversion,
        core: this.activityStatistics === 'extreme',
        startDate: this.reportPeriod.start,
        endDate: this.reportPeriod.end,
        bab: this.bab,
        utm: this.campaign ? this.campaign.utm : null,
        bamp: this.applyData.bAMP,
        utid: this.utid,
        abTestType: this.abTestType,
        channel: this.campaign ? this.campaign.channel : null,
        cid: this.campaign ? this.campaign.cid : null
      }

      if (params.bab === null) {
        delete params.bab
      }

      if ((this.mode === 'cta' || this.mode === 'only') && this.ctaData) {
        if (this.ctaData.coords) {
          params.ctaDom = this.ctaData.coords.selector

          if (this.ctaData.except) {
            params.ctaExcept = true
          } else {
            params.ctaExcept = false
          }
        }
      }

      if (params.abTestType === null) {
        delete params.abTestType
      }

      return params
    },
    apiParams () {
      const params = {
        ...this.defaultParams,
        event: this.eventType,
        utm: this.campaign ? this.campaign.utm : null,
        utid: this.utid,
        channel: this.campaign ? this.campaign.channel : null,
        cid: this.campaign ? this.campaign.cid : null
      }
      switch (this.eventSource) {
        case 'new':
          params.existing = false
          break
        case 'return':
          params.existing = true
          break
      }

      if (this.eventType === this.getDefaultEventType()) {
        params.hidden = false
      }

      if (this.hourData) {
        if (this.hourData === '0') {
          params.hour = '00'
        } else {
          params.hour = this.hourData
        }
      } else {
        if (JSON.parse(localStorage.getItem('paramData'))) {
          params.hour = JSON.parse(localStorage.getItem('paramData')).timeSeriesData
        }
      }

      return params
    },
    captureImageParams () {
      return {
        aid: this.captureAid || this.aid,
        device: this.device,
        startDate: this.reportPeriod.start,
        endDate: this.reportPeriod.end
      }
    },
    avgFoldParams () {
      const params = { ...this.defaultParams }

      if (this.mode === 'cta') {
        delete params.ctaDom
        delete params.ctaExcept
      }

      return params
    },
    streamParams () {
      const params = {
        ...this.apiParams
      }

      delete params.event
      return params
    },
    dailyDeviceParams () {
      const params = {
        aid: this.aid,
        device: this.device,
        startDate: this.reportPeriod.start,
        endDate: this.reportPeriod.end,
        refer: this.referer,
        abTestType: this.abTestType
      }

      if (params.abTestType === null) {
        delete params.abTestType
      }

      return params
    },
    hasInfoBar () {
      return (this.showCtaTip && !this.applyData.bAMP) || (this.scrollTip)
    },
    fileName () {
      let fileName = ''

      fileName += this.applyData.pageName.replace(/&/gi, '%26').replace(/%/gi, '%25').replace(/[\t\\/:*?"'<>|]/gi, '')

      fileName += '_'
      fileName += this.reportPeriod.start.replace(/[-]/gi, '') + '-' + this.reportPeriod.end.replace(/[-]/gi, '')

      fileName += '_'
      fileName += this.device.charAt(0).toUpperCase()

      fileName += '_'
      if (this.refererData) {
        if (this.referer === 'all') {
          fileName += 'All'
        } else {
          const refererIndex = this.refererData.findIndex(item => item.refer === this.referer) + 1
          const countSuffix = ['th', 'st', 'nd', 'rd', 'th', 'th', 'th', 'th', 'th', 'th']
          fileName += refererIndex + countSuffix[refererIndex % 10]
        }
      } else {
        fileName += 'All'
      }

      fileName += '_'
      let eventSource = this.eventSource === 'return' ? 'ret' : this.eventSource
      fileName += eventSource.charAt(0).toUpperCase() + eventSource.slice(1, 3)

      fileName += '_'
      if (this.eventType === 'stream') {
        fileName += 'P'
      } else if (this.eventType === 'zoom') {
        fileName += ['Z-I', 'Z-O'][this.realHeatmapType]
      } else if (this.eventType === 'swipe') {
        fileName += ['SW-V', 'SW-H'][this.realHeatmapType]
      } else {
        fileName += this.eventType.charAt(0).toUpperCase()
      }

      if (this.abData) {
        fileName += '_'
        fileName += this.abData._id ? 'A' : 'B'
      }
      if (this.ctaData && this.ctaData.coords) {
        fileName += '_'
        if (this.ctaData.except) {
          fileName += 'Unclicked'
        } else {
          fileName += 'Clicked'
        }
      }
      if (this.eventType === 'stream') {
        fileName += '_'
        if (this.activityStatistics === 'extreme') {
          fileName += 'FAN'
        } else {
          fileName += 'Avg'
        }
        fileName += '_'
        if (!this.conversion && this.conversion !== false) {
          fileName += 'All'
        } else {
          if (this.conversion) {
            fileName += 'Con'
          } else {
            fileName += 'Non'
          }
        }
      }

      fileName += '.png'
      return fileName
    },
    totalPv () {
      return (this.metric.pv === null && !this.isTotalPv) ||
        (this.activeSelector && !this.isTotalPv) ||
        (this.coordsClick && !this.isTotalPv) ||
        (this.userActionData && (this.userActionData.hoverIndex !== null || this.userActionData.activeIndex !== null) && !this.isTotalPv) ||
        (this.metric.pv === 0 && this.isHeatmapOnHover)
        ? null
        : this.freqTotalPv !== null
          ? thousand(this.freqTotalPv)
          : thousand(this.metric.pv)
    }
  },
  watch: {
    aid () {
      this.resetCtaData()
      this.$nextTick().then(() => {
        this.load(true)
      })
    },
    reportPeriod (value) {
      this.resetCtaData()
      this.$nextTick().then(() => {
        this.campaign = null
        this.load(true)
        if (this.isFrequency) {
          this.freqTotalPv = null
          this.onFrequency(false)
        }
        this.$store.commit('clearTimeseriesData')
      })
    },
    captureImageTimestamp (value) {
      this.$nextTick().then(() => {
        this.load()
        this.freqTotalPv = null
        this.onFrequency(false)
        this.$store.commit('clearFreqSelect')
        this.$store.commit('clearFreqRangeSelect')
      })
    },
    ctaData (value, from) {
      this.setCta(value, from)
    },
    timeseriesData () {
      if (this.timeseriesData === null) {
        this.load()
      } else {
        this.$nextTick(() => {
          this.loadHourBasicInfoData()
          this.loadData()
          this.loadFreqData()
          this.setCta(this.ctaData, {})
          this.freqTotalPv = null
          this.onFrequency(false)
          this.$store.commit('clearFreqSelect')
          this.$store.commit('clearFreqRangeSelect')
        })
      }
    },
    device () {
      if (!this.init) {
        this.$nextTick(() => {
          this.freqTotalPv = null
          this.onFrequency(false)
          this.$store.commit('clearFreqSelect')
          this.$store.commit('clearFreqRangeSelect')
          this.loadFreqData()
        })
      }
    },
    eventSource () {
      if (!this.init) {
        this.$nextTick(() => {
          this.freqTotalPv = null
          this.onFrequency(false)
          this.$store.commit('clearFreqSelect')
          this.$store.commit('clearFreqRangeSelect')
          this.loadFreqData()
        })
      }
    },
    utid () {
      this.$nextTick(() => {
        this.freqTotalPv = null
        this.onFrequency(false)
        this.$store.commit('clearFreqSelect')
        this.$store.commit('clearFreqRangeSelect')
        this.loadFreqData()
      })
    },
    campaign () {
      this.$nextTick(() => {
        if (this.campaign) {
          this.freqTotalPv = null
          this.onFrequency(false)
          this.$store.commit('clearFreqSelect')
          this.$store.commit('clearFreqRangeSelect')
          this.loadFreqData()
        } else {
          this.load()
        }
      })
    },
    isFrequency () {
      if (this.isFrequency) {
        this.loadFreqData()
      } else {
        this.$nextTick(() => {
          this.$emit('onFreqPvChange', null)
          this.loadData()
          this.setFreqRange(false)
          this.freqTotalPv = null
        })
      }
    },
    freqPv () {
      if (!this.freqPv) this.freqTotalPv = null
      this.$nextTick().then(() => {
        // this.load()
        this.loadData()
        // this.loadHeatmapData()
        this.getTotalPv(this.freqData)
      })
    },
    heatmapType (value) {
      let message = {
        [this.mode]: {
          heatmapType: value
        }
      }
      this.realHeatmapType = value
      this.$emit('update:syncedOptions', message)
    },
    conversion (value) {
      this.onOptionChange('conversion', value)
    },
    activityStatistics (value) {
      this.onOptionChange('activityStatistics', value)
    },
    referer (value) {
      this.$emit('onRefererChange', value)
      this.freqTotalPv = null
      this.onFrequency(false)
      this.$store.commit('clearFreqSelect')
      this.$store.commit('clearFreqRangeSelect')
      this.loadFreqData()
    },
    isLoading (value) {
      if (value) {
        this.$emit('onHeatmapLoadStart', this.mode)
      } else {
        this.$emit('onHeatmapLoadEnd', this.mode)
      }
    },
    syncedOptions (value, prev) {
      let message

      if (prev === null && value &&
        (this.mode === 'aType' || this.mode === 'compareA')) {
        message = {
          [this.mode]: {
            device: this.device,
            eventType: this.eventType,
            eventSource: this.eventSource,
            heatmapType: this.realHeatmapType
          }
        }

        this.$emit('update:syncedOptions', message)
      } else if (value) {
        if (!value[this.mode]) {
          const changed = Object.values(value)[0]

          if (changed.fitMode) {
            this.onFitModeChange(changed.fitMode, true)
            delete changed.fitMode
          }
          if ([0, 1].includes(changed.heatmapType)) {
            this.setHeatmapType(changed.heatmapType, true)
            delete changed.heatmapType
          }
          this.changeOptions(changed)
        }
      }
    }
  },
  created () {
    EventBus.$on('onCampaignChange', (campaign) => {
      let change = false
      if (JSON.stringify(this.campaign) !== JSON.stringify(campaign)) {
        change = true
      }
      if (change) {
        this.campaign = campaign
        this.device = null
        this.eventType = null
        this.load(true)
      }
    })
    EventBus.$emit('onCampaignChange', null)
    this.campaign = null

    EventBus.$off('onUtChange')
    EventBus.$on('onUtChange', (utid) => {
      let change = false
      if (this.utid !== utid) {
        change = true
      }
      this.utid = utid
      this.device = null
      this.eventType = null
      if (change) {
        this.load(true)
      }
    })
    EventBus.$emit('onUtButtonChange', null)

    this.utid = this.initialUtid || null
    if (this.initialUtm) {
      this.campaign = {
        utm: this.initialUtm
      }
    }

    this.load()
  },
  mounted () {
    EventBus.$on('showHtmldiff', (value) => {
      this.goUnder = value
    })
  },
  methods: {
    ...mapMutations([
      'setFreqRange'
    ]),
    getTotalPv (data) {
      const freqPv = JSON.parse(localStorage.getItem('paramData')) ? JSON.parse(localStorage.getItem('paramData')).freqPv : null
      if (!data || !this.freqPv) {
        this.freqTotalPv = null
      }
      if (this.freqRange) {
        this.freqData.sort((a, b) => a - b)
      }
      for (let i = 0; i < data.length; i++) {
        if (this.freqRange) {
          let slicePv = null
          if (this.startIdx > this.endIdx) {
            slicePv = data.slice(this.endIdx, this.startIdx + 1)
            this.freqTotalPv = slicePv.reduce((acc, cur) => {
              return acc + cur.totalPv
            }, 0)
          } else {
            slicePv = data.slice(this.startIdx, this.endIdx + 1)
            this.freqTotalPv = slicePv.reduce((acc, cur) => {
              return acc + cur.totalPv
            }, 0)
          }
        } else {
          if (this.startIdx === i) {
            this.freqTotalPv = data[i].totalPv
          } else if (freqPv) {
            if (freqPv.length === 1) {
              if (data[i].pv === String(freqPv[0])) {
                this.freqTotalPv = data[i].totalPv
              }
            } else {
              let totalPv = null
              freqPv.forEach(num => {
                data.forEach(item => {
                  // 범위 구분(예: "5-10")
                  if (item.pv.includes('-')) {
                    const range = item.pv.split('-').map(Number)
                    if (num >= range[0] && num <= range[1]) {
                      totalPv += item.totalPv
                    }
                  } else if (parseInt(item.pv) === num) { // 단일 숫자 구분(예: "3")
                    totalPv += item.totalPv
                  }
                })
              })
              this.freqTotalPv = totalPv
            }
          }
        }
      }

      this.$emit('onFreqPvChange', this.freqTotalPv)
    },
    showScrollTip () {
      if (this.scrollData && this.scrollData.docHeight && this.scrollTip === null) {
        const calcHeight = this.scrollData.docHeight.max - this.scrollData.docHeight.min
        switch (this.device) {
          case DEVICE_DESCKTOP:
            if (calcHeight > DESKTOP_SCROLL_HEIGHT) {
              this.scrollTip = true
            } else {
              this.scrollTip = false
            }
            break
          case DEVICE_TABLET || DEVICE_PHONE:
            if (calcHeight > MOBILE_SCROLL_HEIGHT) {
              this.scrollTip = true
            } else {
              this.scrollTip = false
            }
            break
        }
      } else {
        this.scrollTip = false
      }
      return this.scrollTip
    },
    load (needSegmentReset) {
      if (needSegmentReset) {
        this.device = this.getDefaultDevice()
        this.eventSource = 'all'
        this.referer = 'all'
      }

      const params = {
        aid: this.aid,
        startDate: this.reportPeriod.start,
        endDate: this.reportPeriod.end,
        bab: this.bab,
        channel: this.campaign ? this.campaign.channel : null,
        cid: this.campaign ? this.campaign.cid : null,
        utm: this.campaign ? this.campaign.utm : null,
        utid: this.utid,
        abTestType: this.abTestType
      }
      if (this.freqPv && this.isFrequency) {
        params.freq = this.freqPv
      } else if (JSON.parse(localStorage.getItem('paramData'))) {
        params.freq = JSON.parse(localStorage.getItem('paramData')).freqPv
      } else {
        params.freq = null
      }

      if (params.bab === null) {
        delete params.bab
      }
      if (params.utid === null) {
        delete params.utid
      }
      if (params.abTestType === null) {
        delete params.abTestType
      }

      getBasicInfoData(params).then(result => {
        this.infoData = result
        this.$emit('onInfoData', result)
        if (result.hour) {
          this.$emit('onTimeSeries', this.fillMissingWithEmptyArrays(result.hour))
        }

        if (!this.device) {
          this.device = this.getDefaultDevice()
        }

        if (!this.eventType) {
          this.eventType = this.getDefaultEventType()
        } else {
          this.getNewMobileCheck()
        }
        const compareReferer = this.getCompareReferer()

        if (compareReferer) {
          this.referer = compareReferer
        }
        this.loadCaptureImage()
        // this.loadFreqData()
        // this.loadHourBasicInfoData()
        // this.loadTextHeatmapData()

        if (this.ctaData) {
          this.setCta(this.ctaData, {})
        }
        this.$store.commit('clearTimeseriesData')
        this.init = false
      }).catch(err => {
        console.error(err)
      })
    },
    fillMissingWithEmptyArrays (arr) {
      const result = []
      let expected = 0 // "00"부터 시작

      for (let i = 0; i <= 23; i++) {
        const expectedString = expected.toString().padStart(2, '0')
        // 현재 expectedString이 arr의 어느 요소의 두 번째 값과도 일치하지 않으면
        if (!arr.some(innerArr => innerArr[1] === expectedString)) {
          result.push([0, 'notyet']) // 빈 배열 삽입
        } else {
          result.push(arr.find(innerArr => innerArr[1] === expectedString))
        }
        expected++
      }

      const isNotYet = result.every(time => time[1] === 'notyet')

      if (isNotYet) {
        result.splice(0)
      }

      return result
    },
    setCta (value, from) {
      if (value.coords === null) {
        this.showCtaTip = true
      } else {
        this.showCtaTip = false
      }

      this.ctaPv = null

      if (value.coords) {
        Promise.all([
          this.loadCtaData(),
          this.loadDailyDevice()
        ]).then(result => {
          const ctaReportData = {
            image: {
              url: this.imageUrl,
              width: this.captureWidth,
              height: this.captureHeight
            },
            device: this.device,
            eventSource: this.eventSource,
            data: result[0],
            dailyDevice: result[1],
            infoData: this.infoData
          }
          this.ctaPv = ctaReportData.data.ctaData.pvTotal

          this.ctaReportData = ctaReportData
          this.$emit('onCtaReportData', ctaReportData)
          this.loadData()
        }).catch(console.error)
      } else if (from.coords) {
        this.eventType = this.getDefaultEventType()
        this.loadData()
      }
    },
    getNewMobileCheck () {
      let eventType = this.eventType
      let device = this.device
      let bAMP = this.applyData.bAMP
      let bNewMobile = true
      if (this.reportPeriod.start < NEW_MOBILE_HEATMAP_STARTDATE) {
        bNewMobile = false
      }

      if (device === 'desktop') {
        if (eventType === 'tap') {
          this.eventType = 'click'
        }
        if (eventType === 'zoom') {
          this.eventType = 'move'
        }
      } else {
        if (bAMP) {
          if (eventType === 'tap') {
            this.eventType = 'click'
          }
          if (eventType === 'zoom') {
            this.eventType = 'move'
          }
        } else {
          if (bNewMobile) {
            if (eventType === 'click' || eventType === 'stream') {
              this.eventType = 'tap'
            }
            if (eventType === 'stream') {
              this.eventType = 'swipe'
            }
            if (eventType === 'move') {
              this.eventType = 'zoom'
            }
          } else {
            if (eventType === 'tap') {
              this.eventType = 'click'
            }
            if (eventType === 'zoom' || eventType === 'swipe') {
              this.eventType = 'move'
            }
          }
        }
      }
    },
    getDefaultDevice () {
      if (this.initialDevice) {
        return this.initialDevice
      } else if (this.applyData.bMobile) {
        return 'phone'
      } else {
        const devicePvList = ['desktop', 'tablet', 'phone'].map(device => {
          return {
            device,
            pv: this.infoData.device[device] ? this.infoData.device[device].pv : 0
          }
        })
        devicePvList.sort((a, b) => b.pv - a.pv)
        if (this.eventType === 'swipe') {
          if (devicePvList[0].device === 'desktop') {
            this.eventType = 'move'
          }
        }
        return devicePvList[0].device
      }
    },
    getDefaultEventType () {
      if (this.initialEventType) {
        return this.initialEventType
      }

      if (this.applyData.bAMP) {
        return 'click'
      }

      if (this.device === 'desktop') {
        return 'click'
      } else {
        if (this.reportPeriod.start >= NEW_MOBILE_HEATMAP_STARTDATE) {
          return 'tap'
        } else {
          return 'click'
        }
      }
    },
    resetContainerScroll () {
      if (this.$refs.container) {
        this.$refs.container.scrollTop = 0
      }
    },
    getCompareReferer () {
      if (this.mode !== 'compareB') {
        return null
      }

      const deviceData = this.infoData.device[this.device]

      if (deviceData && deviceData.refer && deviceData.refer.length) {
        return deviceData.refer[0].refer
      }
      return null
    },
    initCaptureImageContainer (imageUrl) {
      if (!imageUrl) {
        this.captureWidth = 0
        this.captureHeight = 0
        this.setContainerSize()
        this.loadData()
      } else {
        const img = document.createElement('img')
        img.addEventListener('load', event => {
          const el = event.target
          this.captureWidth = el.width
          this.captureHeight = el.height
          this.setContainerSize()
          this.loadData()
        })
        img.src = imageUrl
      }
    },
    loadCaptureImage () {
      this.clearData()
      this.isLoading = true
      const params = this.captureImageParams

      getCaptureImageUrl(params, this.captureImageTimestamp).then(imageUrl => {
        this.imageUrl = imageUrl
        this.initCaptureImageContainer(this.imageUrl)
      }).catch(err => {
        console.error(err)
        this.showHeatmapDialog('noPVByDevice')
        this.isLoading = false
        this.$emit('onHeatmapData', this.eventType, null)
      })
    },
    setHeatmapType (type, fromSynced) {
      this.realHeatmapType = type
      this.$emit('setHeatmapType', type)

      if (!fromSynced && this.syncedOptions) {
        this.$emit('update:syncedOptions', {
          [this.mode]: {
            heatmapType: type
          }
        })
      }
    },
    setContainerSize () {
      const elRect = this.$refs.container.getBoundingClientRect()
      let elWidth = elRect.width
      let elHeight = elRect.height
      let defaultContainerWidth
      const DEFAULT_MARGIN = 40

      if (this.mode === 'only') {
        defaultContainerWidth = elWidth
      } else if (this.device === 'phone') {
        defaultContainerWidth = elWidth - 302
      } else {
        defaultContainerWidth = elWidth - DEFAULT_MARGIN
      }

      if (this.fitMode === 'width') {
        this.containerWidth = defaultContainerWidth
        this.containerHeight = this.ratio ? this.containerWidth / this.ratio : 0
        this.containerScale = 1
      } else {
        this.containerHeight = elHeight - DEFAULT_MARGIN
        this.containerWidth = this.ratio ? this.containerHeight * this.ratio : 0
        this.containerScale = this.containerWidth / defaultContainerWidth
      }
    },
    changeOptions (changed) {
      if (!changed || !Object.keys(changed).length) {
        return
      }
      let requireImageReload = false
      let requireCtaReset = false

      let realChanged = {}
      for (const [key, value] of Object.entries(changed)) {
        if (this[key] && this[key] !== changed[key]) {
          realChanged[key] = value
        }
      }

      for (const [key, value] of Object.entries(realChanged)) {
        this[key] = value

        switch (key) {
          case 'device':
            requireImageReload = true
            requireCtaReset = true
            const compareReferer = this.getCompareReferer()
            this.referer = compareReferer || 'all'
            // this.onFrequency(false)
            // this.$store.commit('clearFreqSelect')
            // this.$store.commit('clearFreqRangeSelect')
            break
          case 'referer':
          case 'eventSource':
            requireCtaReset = true
            // this.onFrequency(true)
            break
        }
      }

      if (requireCtaReset) {
        this.resetCtaData()
      }

      if (requireImageReload) {
        this.loadCaptureImage()
      } else {
        this.loadData()
      }
    },
    onOptionChange (option, value) {
      if (this.mode === 'cta' && option === 'eventType' && value !== 'click' && !this.ctaData.coords) {
        this.showHeatmapDialog('noCtaCoords')
        return
      }

      let changed = {
        [option]: value
      }

      if (option === 'device') {
        if (value === 'desktop') {
          if (this.eventType === 'tap') {
            this.eventType = 'click'
            changed.eventType = 'click'
          } else if (this.eventType === 'zoom' || this.eventType === 'swipe') {
            this.eventType = 'move'
            changed.eventType = 'move'
          }
        } else {
          if (this.applyData.bAMP) {
            if (this.eventType === 'tap') {
              this.eventType = 'click'
              changed.eventType = 'click'
            } else if (this.eventType === 'zoom' || this.eventType === 'swipe') {
              this.eventType = 'move'
              changed.eventType = 'move'
            }
          } else {
            if (this.eventType === 'click') {
              if (this.reportPeriod.start >= NEW_MOBILE_HEATMAP_STARTDATE) {
                this.eventType = 'tap'
                changed.eventType = 'tap'
              } else {
                this.eventType = 'click'
                changed.eventType = 'click'
              }
            } else if (this.eventType === 'move') {
              if (this.reportPeriod.start >= NEW_MOBILE_HEATMAP_STARTDATE) {
                this.eventType = 'zoom'
                changed.eventType = 'zoom'
              } else {
                this.eventType = 'move'
                changed.eventType = 'move'
              }
            } else if (this.eventType === 'stream') {
              if (value !== 'desktop') {
                if (this.reportPeriod.start >= NEW_MOBILE_HEATMAP_STARTDATE) {
                  this.eventType = 'tap'
                  changed.eventType = 'tap'
                } else {
                  this.eventType = 'click'
                  changed.eventType = 'click'
                }
              }
            }
          }
        }
      }

      this.changeOptions(changed)

      if (option !== 'referer' && this.syncedOptions) {
        this.$emit('update:syncedOptions', {
          [this.mode]: {
            ...changed
          }
        })
      }
    },
    clearData () {
      this.$emit('onActiveSelectorChange', null)
      this.$emit('onUserActionDataChange', null)
      if (!this.initialHeatmapType && this.initialHeatmapType !== 0) {
        this.setHeatmapType(0)
      }
      this.dialogType = null
      this.avgFoldData = null
      this.svgData = null
      this.gridData = null
      this.coordsData = null
      this.swipeData = null
      this.scrollData = null
      this.streamData = null
      this.isCaptureHeightExceed = false
      this.resetContainerScroll()
    },
    loadData () {
      this.clearData()
      this.isLoading = true
      const isCaptureHeightExceed = this.captureHeight >= 32767

      if ((this.mode === 'aType' || this.mode === 'bType') && this.abTestType === 'text') {
        this.loadTextHeatmapData()
      }

      this.reportData = {
        image: {
          url: this.imageUrl,
          width: this.captureWidth,
          height: this.captureHeight
        },
        device: this.device,
        eventSource: this.eventSource,
        conversion: this.conversion,
        activityStatistics: this.activityStatistics,
        data: null
      }

      let pv = this.metric.pv

      if (!pv) {
        this.showHeatmapDialog('noPVByDevice')
        this.isCaptureHeightExceed = isCaptureHeightExceed
        this.isLoading = false
        this.$emit('onHeatmapData', this.eventType, this.reportData)
        return
      }

      const startDate = startOfDay(parse(this.reportPeriod.start))
      const endDate = endOfDay(parse(this.reportPeriod.end))
      const streamMinDate = startOfDay(parse(this.reportData.activityStatistics === 'extreme' ? '2019-12-01' : '2019-09-30'))

      this.$nextTick().then(() => {
        const tasks = []
        if (this.device !== 'desktop') {
          this.showMobileTip = true
        }
        switch (this.eventType) {
          case 'click':
          case 'move':
            tasks.push(this.loadAvgFold())
            tasks.push(this.loadHeatmapData())
            break
          case 'tap':
          case 'zoom':
            tasks.push(this.loadAvgFold())
            tasks.push(this.loadGridData())
            break
          case 'swipe':
            tasks.push(this.loadAvgFold())
            tasks.push(this.loadSwipeData())
            break
          case 'scroll':
            if (this.device !== 'desktop') {
            }
            tasks.push(this.loadAvgFold())
            tasks.push(this.loadScrollData())
            break
          case 'stream':
            if (endDate < streamMinDate) {
              tasks.push(Promise.reject(new Error('STREAM_UNSUPPORTED_DATE')))
            } else {
              tasks.push(this.loadStreamData())
            }
            break
        }
        Promise.all(tasks).then(result => {
          switch (this.eventType) {
            case 'click':
            case 'tap':
            case 'move':
            case 'zoom':
            case 'swipe':
            case 'stream':
              this.reportData.data = result[result.length - 1]
              break
            case 'scroll':
              this.reportData.data = {
                avgFold: result[0],
                scroll: result[1]
              }
              break
          }

          this.getNewMobileCheck()

          if (['click', 'tap', 'move'].includes(this.eventType)) {
            if (!this.svgData || !Object.keys(this.svgData).length) {
              if (this.eventType === 'click') {
                this.showHeatmapDialog('noClickEvent')
              } else if (this.eventType === 'tap') {
                this.showHeatmapDialog('noTapEvent')
              } else if (this.eventType === 'move') {
                this.showHeatmapDialog('noMoveEvent')
              }
              this.reportData.data = null
            }
          } else if (this.eventType === 'zoom') {
            if (!this.gridData || !Object.keys(this.gridData).length ||
              !(this.gridData[0] && this.gridData[1]) || (
              !Object.keys(this.gridData[0]).length &&
              !Object.keys(this.gridData[1]).length
            )) {
              this.showHeatmapDialog('noZoomEvent')
              this.reportData.data = null
            }
          } else if (this.eventType === 'swipe') {
            if (!this.swipeData || !Object.keys(this.swipeData).length ||
              !(this.swipeData[0] && this.swipeData[1]) || (
              !(this.swipeData[0].left && this.swipeData[0].left.length > 0) &&
              !(this.swipeData[0].right && this.swipeData[0].right.length > 0) &&
              !(this.swipeData[1].left && this.swipeData[1].left.length > 0) &&
              !(this.swipeData[1].right && this.swipeData[1].right.length > 0)
            )) {
              this.showHeatmapDialog('noSwipeEvent')
              this.reportData.data = null
            }
          } else if (this.eventType === 'scroll' && !this.reportData.data.scroll) {
            this.showHeatmapDialog('noEnoughPv')
            this.reportData.data = null
          } else if (this.eventType === 'stream') {
            const hasUnsupportedDate = startDate < streamMinDate

            if (!this.reportData.data) {
              if (hasUnsupportedDate) {
                this.showHeatmapDialog('noStreamEventIncludeNoStreamDate')
              } else {
                this.showHeatmapDialog('noStreamEvent')
              }
            } else {
              if (hasUnsupportedDate) {
                this.showHeatmapDialog('includeNoStreamDate')
              } else {
                if (this.reportData.data.hasNoCoordsItem && !this.reportData.data.path.length) {
                  this.showHeatmapDialog('noStreamDisplay')
                }
              }
            }
          }

          this.isCaptureHeightExceed = isCaptureHeightExceed
          this.$emit('onHeatmapData', this.eventType, this.reportData)
          this.$nextTick().then(() => {
            this.isLoading = false
          })
        }).catch(err => {
          if (err.message === 'NO_SCROLL_DATA') {
            this.showHeatmapDialog('noEventByDate')
            this.isCaptureHeightExceed = isCaptureHeightExceed
            this.$emit('onHeatmapData', this.eventType, null)
          } else if (err.message === 'STREAM_UNSUPPORTED_DATE') {
            this.showHeatmapDialog('noStreamByDate')
            this.$emit('onHeatmapData', this.eventType, { data: null })
          } else {
            console.error(err)
          }
          this.isLoading = false
        })
      })
    },
    loadAvgFold () {
      const params = this.avgFoldParams

      return getAvgFoldData(params).then(data => {
        this.avgFoldData = data
        return this.avgFoldData
      })
    },
    loadHeatmapData () {
      this.isLoading = true
      const params = {
        ...this.apiParams
      }

      if (this.freqPv && this.isFrequency) {
        params.freq = this.freqPv
      } else if (JSON.parse(localStorage.getItem('paramData'))) {
        params.freq = JSON.parse(localStorage.getItem('paramData')).freqPv
      }

      if (params.event === 'tap') {
        params.event = 'tab'
      }

      return getHeatmapData(params).then(data => {
        this.isLoading = false
        this.svgData = heatmapDataCheckByCapture(data.heatmap, this.captureWidth, this.captureHeight)
        this.$emit('onLoadingABSuccess')
        let totalPv
        if (this.ctaPv !== null) {
          totalPv = this.ctaPv
        } else if (this.freqTotalPv) {
          totalPv = this.freqTotalPv
        } else {
          totalPv = this.metric.pv
        }
        this.coordsData = normalizeClickData(data, totalPv, this.device,
          this.captureWidth, this.captureHeight)
        return this.coordsData
      })
    },
    loadTextHeatmapData () {
      const params = this.apiParams
      params.orgAid = this.bab ? null : this.orgAid

      return getCoordsData(params).then(data => {
        this.svgData = data.heatmap
        this.$emit('onLoadingABSuccess')
        const totalPv = this.ctaPv !== null ? this.ctaPv : this.metric.pv
        this.coordsDataText = normalizeTextData(data, totalPv, this.device,
          this.captureWidth, this.captureHeight)
        return this.coordsData
      })
    },
    loadGridData () {
      const params = {
        ...this.apiParams
      }

      if (this.freqPv && this.isFrequency) {
        params.freq = this.freqPv
      } else if (JSON.parse(localStorage.getItem('paramData'))) {
        params.freq = JSON.parse(localStorage.getItem('paramData')).freqPv
      }

      if (params.event === 'tap') {
        params.event = 'tab'

        return getHeatmapData(params).then(data => {
          this.svgData = heatmapDataCheckByCapture(data.tabHeatmap, this.captureWidth, this.captureHeight)
          this.gridData = null
          this.gridData = [data.doubletabHeatmap]
          let totalPv
          if (this.ctaPv !== null) {
            totalPv = this.ctaPv
          } else if (this.freqTotalPv) {
            totalPv = this.freqTotalPv
          } else {
            totalPv = this.metric.pv
          }
          data.topClicks = data.topTabs
          this.coordsData = normalizeClickData(data, totalPv, this.device,
            this.captureWidth, this.captureHeight)
          return this.coordsData
        })
      } else if (params.event === 'zoom') {
        return Promise.all([
          getHeatmapData({
            ...params,
            event: 'zoomin'
          }),
          getHeatmapData({
            ...params,
            event: 'zoomout'
          })
        ]).then(data => {
          this.svgData = null
          this.gridData = null
          if (!this.initialHeatmapType && this.initialHeatmapType !== 0) {
            this.setHeatmapType(0)
          }
          const zoomInData = heatmapDataCheckByCapture(data[0].heatmap, this.captureWidth, this.captureHeight)
          const zoomOutData = heatmapDataCheckByCapture(data[1].heatmap, this.captureWidth, this.captureHeight)

          this.gridData = [zoomInData, zoomOutData]
          const totalPv = this.ctaPv !== null ? this.ctaPv : this.metric.pv
          data[0].topZoomin = data[0].topZooms
          data[0].topZoomout = data[1].topZooms
          const mergedData = {
            ...data[0],
            dom: {
              ...data[0].dom,
              ...data[1].dom
            }
          }
          this.coordsData = normalizeClickData(mergedData, totalPv, this.device,
            this.captureWidth, this.captureHeight)
          return this.coordsData
        })
      }
    },
    loadSwipeData () {
      const params = {
        ...this.apiParams
      }

      if (this.freqPv && this.isFrequency) {
        params.freq = this.freqPv
      } else if (JSON.parse(localStorage.getItem('paramData'))) {
        params.freq = JSON.parse(localStorage.getItem('paramData')).freqPv
      }

      return getSwipeData(params).then(data => {
        if (!data || Object.entries(data).length <= 1) {
          throw new Error('NO_SCROLL_DATA')
        }
        if (!this.initialHeatmapType && this.initialHeatmapType !== 0) {
          this.setHeatmapType(0)
        }

        const makeMoreSwipeData = (rawData) => {
          rawData = normalizeSwipeData(rawData)
          let dataMade = [{
            left: [],
            right: []
          }, {
            left: [],
            right: []
          }]
          for (let i = 0; i <= Math.floor(this.captureHeight / 100) + 1; i++) {
            for (let j = 0; j < 2; j++) {
              for (const position of ['left', 'right']) {
                dataMade[j][position].push(rawData[j][position][i] || 0)
              }
            }
          }
          for (let j = 0; j < 2; j++) {
            for (const position of ['left', 'right']) {
              dataMade[j][`${position}Total`] = dataMade[j][position].reduce((a, b) => a + b, 0)
            }
            dataMade[j].min = dataMade[j].left.concat(dataMade[j].right).reduce((a, b) => Math.min(a, b), Infinity)
            dataMade[j].max = dataMade[j].left.concat(dataMade[j].right).reduce((a, b) => Math.max(a, b), 0)
            for (const position of ['left', 'right']) {
              dataMade[j][`${position}Percent`] = dataMade[j][position].map((a) => (dataMade[j][`${position}Total`] > 0 ? (a / dataMade[j][`${position}Total`]) : 0))
              dataMade[j][`${position}Rate`] = dataMade[j][position].map((a) => (dataMade[j].max !== dataMade[j].min ? ((a - dataMade[j].min) / (dataMade[j].max - dataMade[j].min)) : 0))
            }
          }
          return dataMade
        }

        this.swipeData = makeMoreSwipeData(data)
        return this.swipeData
      })
    },
    loadScrollData () {
      const params = {
        ...this.apiParams
      }

      if (this.freqPv && this.isFrequency) {
        params.freq = this.freqPv
      } else if (JSON.parse(localStorage.getItem('paramData'))) {
        params.freq = JSON.parse(localStorage.getItem('paramData')).freqPv
      }

      return getScrollData(params).then(data => {
        if (!data || Object.entries(data).length <= 1) {
          throw new Error('NO_SCROLL_DATA')
        }
        this.scrollData = normalizeScrollData(data, this.captureHeight, params)
        this.showScrollTip()
        return this.scrollData
      })
    },
    loadStreamData () {
      const params = {
        ...this.streamParams
      }

      if (this.freqPv && this.isFrequency) {
        params.freq = this.freqPv
      } else if (JSON.parse(localStorage.getItem('paramData'))) {
        params.freq = JSON.parse(localStorage.getItem('paramData')).freqPv
      } else {
        params.freq = null
      }

      let loadStart = Date.now()
      return getStreamPathData(params).then(data => {
        if (!data || !data.cache) {
          let loadEnd = Date.now()
          while (loadEnd - loadStart < 1000) {
            loadEnd = Date.now()
          }
        }

        this.streamData = normalizeStreamData(data, this.captureWidth, this.captureHeight)

        return this.streamData
      })
    },
    loadCtaData () {
      const params = this.apiParams
      return getCtaData(params)
    },
    loadDailyDevice () {
      const params = this.dailyDeviceParams
      return getDailyDevice(params)
    },
    setHeatmapInfoHeight (height) {
      this.heatmapInfoHeight = height
    },
    onHeatmapDownload () {
      this.$emit('onDownloading', true)

      let host = `${location.protocol}//${location.hostname}`
      if (location.port) {
        host += `:${location.port}`
      }

      let heatmapUrl = `${host}/heatmap/${this.aid}/${this.captureAid || this.orgAid || ''}?mode=${this.mode}`
      if (this.abTestType) {
        heatmapUrl = heatmapUrl + `&abTestType=${this.abTestType}`
      }

      const params = {
        paramData: {
          startDate: this.reportPeriod.start,
          endDate: this.reportPeriod.end,
          device: this.device,
          referer: this.referer,
          eventSource: this.eventSource,
          eventType: this.eventType,
          heatmapType: this.heatmapType,
          conversion: this.conversion,
          activityStatistics: this.activityStatistics,
          ctaData: this.ctaData && this.ctaData.coords ? this.ctaData : null,
          lang: this.$i18n.locale,
          utm: this.campaign ? this.campaign.utm : null,
          utid: this.utid,
          timeSeriesData: this.timeseriesData ? this.timeseriesData : null,
          freqPv: this.freqPv ? this.freqPv : null
        },
        aid: this.aid,
        device: this.device,
        userId: this.userData.userId,
        url: heatmapUrl
      }
      if (this.mode === 'aType' || this.mode === 'bType') {
        params.paramData.heatmapType = this.realHeatmapType
      }
      getHeatmapImage(params).then(async result => {
        if (result.data.success) {
          const data = result.data.data
          const downloadUrl = `${BEUSABLY_URL}/viewer/beusable_capture/download/${this.aid}/${data.filename}?filename=${this.fileName}`

          const link = document.createElement('a')
          link.href = downloadUrl
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)

          this.$emit('onDownloading', false)
        } else {
          this.$emit('onDownloading', 'error')
        }
      }).catch(err => {
        console.error(err)
        this.$emit('onDownloading', 'error')
      })
    },
    onFitModeChange (mode, fromSynced) {
      this.fitMode = mode
      this.setContainerSize()

      if (!fromSynced && this.syncedOptions) {
        this.$emit('update:syncedOptions', {
          [this.mode]: {
            fitMode: mode
          }
        })
      }
    },
    onHeatmapCenterChange (center) {
      const container = this.$refs.container
      const containerPadding = 20

      const containerCenter = {
        left: container.offsetWidth / 2 - containerPadding,
        top: container.offsetHeight / 2 - containerPadding
      }
      const offsetTop = Math.max(0, center.top - containerCenter.top)

      if (!this.scroller) {
        this.scroller = new AnimateScroll(container)
      }

      this.scroller.scrollTo(offsetTop, 300)
    },
    resetCtaData () {
      if (this.mode === 'cta') {
        this.$emit('onCtaDataChange', { coords: null, except: false })
      }
    },
    showHeatmapDialog (dialogType) {
      this.dialogType = dialogType
    },
    loadHourBasicInfoData () {
      const params = {
        aid: this.aid,
        startDate: this.reportPeriod.start,
        endDate: this.reportPeriod.end,
        utm: this.campaign ? this.campaign.utm : null
      }

      if (this.hourData !== null) {
        if (this.hourData === '0') {
          params.hour = '00'
        } else {
          params.hour = this.hourData
        }
      } else {
        if (JSON.parse(localStorage.getItem('paramData'))) {
          params.hour = JSON.parse(localStorage.getItem('paramData')).timeSeriesData
        }
      }

      getHourBasicInfoData(params).then(result => {
        this.infoData = result

        this.$emit('onInfoData', this.infoData)
        this.device = this.getDefaultDevice()

        if (!this.eventType) {
          this.eventType = this.getDefaultEventType()
        } else {
          this.getNewMobileCheck()
          this.$emit('onHeatmapData', this.eventType, null)
        }
        this.referer = 'all'
        this.onOptionChange('eventSource', 'all')
        this.loadCaptureImage()

        if (this.ctaData) {
          this.setCta(this.ctaData, {})
        }
      }).catch(err => {
        console.error(err)
      })
    },
    onFrequency (value) {
      this.isFrequency = value
      this.$emit('showFrequency', value)
    },
    loadFreqData () {
      this.isLoading = true
      const params = {
        ...this.apiParams
      }

      if (this.hourData) {
        if (this.hourData === '0') {
          params.hour = '00'
        } else {
          params.hour = this.hourData
        }
      } else {
        if (JSON.parse(localStorage.getItem('paramData'))) {
          params.hour = JSON.parse(localStorage.getItem('paramData')).timeSeriesData
        }
      }
      switch (this.eventSource) {
        case 'new':
          params.existing = false
          break
        case 'return':
          params.existing = true
          break
      }

      getFreqInfoData(params).then(result => {
        if (result.data.length !== 0) {
          this.freqData = result.data.slice().reverse()
          this.freqMeanData = result.mean.toFixed(1)
          this.$emit('onFreqData', this.freqData, this.freqMeanData)
          this.getTotalPv(this.freqData)
        } else {
          this.freqData = null
          this.freqMeanData = null
          this.$emit('onFreqData', this.freqData, this.freqMeanData)
        }
        this.isLoading = false
      }).catch(err => {
        this.isLoading = false
        console.error(err)
      })
    }
  }
}
</script>

<style lang="scss">
.heatmap_area {
  height: 100%;
  box-sizing: border-box;
  position: relative;
  z-index: 0;

  &.under {
    z-index: -1;
  }

  .inner {
    height: 100%;
    overflow: visible;
    border-radius: 4px;
    position: relative;
  }

  .heatmap_tip {
    position: relative;
    height: 34px;
    line-height: 34px;
    font-size: 13px;
    color: #2f2f2f;

    .tip {
      margin-left: 2px;
    }

    i {
      vertical-align: middle;
      margin: -2px 3px 0 7px;
    }

    &.cta_tip, &.scroll_tip {
      background-color: #9fce12;

      .rectangle {
        display: inline-block;
        vertical-align: middle;
        box-sizing: border-box;
        width: 46px;
        height: 16px;
        border-radius: 4px;
        box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.13);
        border: 2px dotted #ffffff;
        margin: -2px 2px 0 2px;
      }
    }

    &.mobile_tip {
      background-color: #f4e439;

      .rectangle {
        display: inline-block;
        vertical-align: middle;
        box-sizing: border-box;
        width: 46px;
        height: 16px;
        border-radius: 4px;
        box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.13);
        border: 2px dotted #ffffff;
        margin: -2px 2px 0 2px;
      }
    }

    &.mobile_tip {
      background-color: #f4e439;

      .rectangle {
        display: inline-block;
        vertical-align: middle;
        box-sizing: border-box;
        width: 46px;
        height: 16px;
        border-radius: 4px;
        box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.13);
        border: 2px dotted #ffffff;
        margin: -2px 2px 0 2px;
      }
    }

    &.stream_tip {
      background-color: #f4e439;

      .tip strong {
        color: $brandColor;
      }
    }
    .close {
      position: absolute;
      top: 9px;
      right: 6px;
      line-height: 15px;
      margin: 0px;
      i {
        background-color: transparent;
      }
    }
  }

  .view-area-wrap {
    position: absolute;
    top: 124px;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
    overflow: hidden;

    &.with_heatmap_downloading {
      &:not(.with_mobile) {
        border-top: 20px solid #151515;

        & /deep/ .heatmap_floating_menu {
          top: -20px;
        }

        .view_wrap {
          margin-top: 0;
        }
      }

      &.with_mobile {
        .heatmap_type {
          top: 0;
          background-color: #000000;
          border-bottom: 21px solid #151515;
        }
      }

      &.with_mobile {
        .heatmap_type {
          top: 0;
          background-color: #000000;
          border-bottom: 21px solid #151515;
        }
      }
    }

    &.with_info_bar {
      top: 158px;
    }

    &.only_mode {
      position: static;
      top: 0;

      .heatmap_type {
        display: none;
      }

      .view_area {
        position: static;
        top: 0;

        &.device-desktop,
        &.device-tablet {
          width: 700px;
        }

        &.device-phone {
          width: 438px;
        }

        .view_wrap {
          margin: 0;

          .view {
            overflow: hidden;
          }
        }
      }
    }

    .floating-menu {
      position: absolute;
      top: 25px;
      right: 26px;
      z-index: 9999998;
      height: 100%;

      &.mobile {
        top: 50px;
      }

      .total-pv {
        min-width: 19px;
        max-height: 20px;
        background-color: #fff;
        border-radius: 3px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 10px;
        cursor: default;
        padding: 5px;
        box-sizing: border-box;

        span {
          color: #EC0047;
          text-align: center;
          font-family: Roboto;
          font-size: 11px;
          font-weight: 600;
          line-height: 100%; /* 11px */

          &.number {
            color: #444;
            text-align: right;
            font-family: Roboto;
            font-size: 11px;
            font-weight: 600;
            line-height: 100%; /* 11px */
          }
        }
      }
    }
  }

  .view_area {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: scroll;
    background: #151515;
    &.hidden-x {
      overflow-x: hidden;
    }
  }

  .view_wrap {
    position: relative;
    z-index: 0;
    margin: 20px auto;
  }

  .view {
    position: relative;
    background-repeat: no-repeat;
    background-size: 100% 100%;
    margin: 0 auto;

    .view-heatmap {
      height: 100%;
      transform-origin: top left;
    }

    &::before {
      opacity: 0.7;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: #282b39;
      content: "";
    }
  }

  .abLabel {
    position: absolute;
    width: 30px;
    height: 30px;
    top: 5px;
    left: 5px;
    line-height: 30px;
    text-align: center;
    border-radius: 4px;
    font-size: 20px;
    font-weight: 500;
    color: #fff;
    background-color: #9fce12;

    &.bLabel {
      background-color: #1199af;
    }
  }

  .loading_indicator {
    position: absolute;
    z-index: 100;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #151515;

    .loading {
      position: relative;
      width: 54px;
      height: 54px;
      margin: 0 auto;
      margin-top: 200px;
    }

    svg {
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      z-index: 1;
      animation: loading 3s linear infinite;

      .loading-inner {
        stroke: {
          dashoffset: 0;
          dasharray: 300;
          width: 20;
          miterlimit: 10;
          linecap: round;
        }

        animation: loading-circle 2s linear infinite;
        stroke: #fff;
        fill: transparent;
      }
    }

    .loading_notice {
      position: absolute;
      left: 50%;
      bottom: -27px;
      transform: translateX(-50%);
      width: 300px;
      word-break: break-all;
      text-align: center;
      font-size: 13px;
      color: #ffffff;
    }

    @keyframes loading {
      0% {
        transform: rotate(0);
      }
      100% {
        transform: rotate(360deg);
      }
    }
    @keyframes loading-circle {
      0% {
        stroke-dashoffset: 0
      }
      100% {
        stroke-dashoffset: -600;
      }
    }
  }

  .heatmap-image--footer {
    background: #2a2a2a;
    height: 40px;
    text-align: right;

    span {
      display: inline-block;
      color: #d7d7d7;
      font-size: 10px;
      vertical-align: middle;
      margin-top: 4px;
      white-space: nowrap;
      width: 300px;
      text-align: right;
    }

    .icon--logo {
      vertical-align: middle;
      width: 95px;
      margin: 14px 0 14px 7px;
      height: 12px;
      background-size: auto 100%;
    }
  }

}
</style>
